import React, { useState, useContext } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Inbox,
  Search,
  Plus,
  History,
  ClipboardList,
  Mail,
  Database,
  X,
  Scan,
  Truck
} from 'lucide-react';
import InboundContext from './InboundContext';
import SendEmailInbound from './SendEmailInbound';
// import { VoiceInterface } from './voice/VoiceInterface';  // Voice AI - DISABLED

// Modern Select Component
const Select = ({ 
  value, 
  onChange, 
  options = [], 
  placeholder = 'Select...', 
  className = '' 
}) => {
  return (
    <select
      value={value}
      onChange={onChange}
      className={`block w-full px-3 py-2 border-2 border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-900 transition-all duration-150 font-medium ${className}`}
    >
      <option value="">{placeholder}</option>
      {options.map((option, index) => (
        <option key={index} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

// Modern Button Component
const Button = ({ 
  children, 
  variant = 'primary', 
  className = '', 
  onClick,
  disabled = false,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm';
  
  const variants = {
    primary: 'bg-gray-900 hover:bg-gray-800 text-white focus:ring-gray-500 border border-gray-900',
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-900 focus:ring-gray-500 border border-gray-300',
    outline: 'border border-gray-900 bg-white hover:bg-gray-900 hover:text-white text-gray-900 focus:ring-gray-500',
    ghost: 'hover:bg-gray-100 text-gray-700 focus:ring-gray-500 border border-transparent'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

// Modern Card Component
const Card = ({ children, className = '', ...props }) => {
  return (
    <div
      className={`bg-white border-2 border-gray-300 shadow-sm ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

function InboundSidebar({ children }) {
  const { clientsData = [] } = useContext(InboundContext);
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const location = useLocation();

  const handleOpenEmailModal = () => {
    setOpenEmailModal(true);
  };

  const handleCloseEmailModal = () => {
    setOpenEmailModal(false);
    setSelectedClient('');
    setClientSearchQuery('');
    setShowClientDropdown(false);
  };

  // Sort clients alphabetically and filter by search query
  const sortedAndFilteredClients = clientsData
    .filter(client =>
      client.email &&
      client.email.trim() !== '' &&
      (client.name?.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
       client.email?.toLowerCase().includes(clientSearchQuery.toLowerCase()))
    )
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const navItems = [
    { to: "/inbounds", label: "Search Inbounds", icon: Search },
    { to: "/inbounds/receiving-scanner", label: "Receiving Scanner", icon: Scan },
    { to: "/inbounds/delivery-receiving", label: "Delivery Receiving", icon: Truck },
    { to: "/inbounds/add-to-shelf", label: "Add to Shelf", icon: Plus },
    { to: "/inbounds/shelf-search", label: "Shelf Search", icon: Search },
    { to: "/inbounds/delivery-log", label: "Delivery Log", icon: ClipboardList },
    { to: "/inbounds/client-shelves", label: "Client Shelves", icon: Inbox },
    { to: "/inbounds/history", label: "History", icon: History },
    { to: "/inbounds/asin-country-database", label: "ASIN Database", icon: Database },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Optimized Professional Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
        {/* Compact Header */}
        <div className="h-16 flex items-center px-3 border-b border-gray-200 bg-gray-900">
          <Inbox className="h-4 w-4 text-white mr-2" />
          <h1 className="text-sm font-semibold text-white">Inbound</h1>
        </div>

        {/* Clean Navigation */}
        <nav className="flex-1 py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to;
            
            return (
              <RouterLink 
                key={item.to} 
                to={item.to} 
                className={`flex items-center px-4 py-2.5 mx-2 my-0.5 text-sm font-medium transition-all duration-150 border-l-4 ${
                  isActive
                    ? 'bg-gray-900 text-white border-l-gray-900'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-l-transparent hover:border-l-gray-300'
                }`}
              >
                <Icon className={`h-4 w-4 mr-3 flex-shrink-0 ${
                  isActive ? 'text-white' : 'text-gray-500'
                }`} />
                <span className="truncate">{item.label}</span>
              </RouterLink>
            );
          })}
        </nav>

        {/* Compact Footer */}
        <div className="p-4 border-t border-gray-200">
          <Button
            onClick={handleOpenEmailModal}
            variant="outline"
            className="w-full justify-center py-2 text-sm"
          >
            <Mail className="h-4 w-4 mr-2" />
            Contact Client
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto bg-gray-50">
        {children}
      </div>

      {/* Voice AI Interface - DISABLED */}
      {/* <VoiceInterface /> */}

      {/* Clean Email Modal */}
      {openEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-md bg-white shadow-lg border border-gray-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Contact Client</h3>
              <button
                onClick={handleCloseEmailModal}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-4" onClick={() => setShowClientDropdown(false)}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search and select client:
                  </label>
                  <div className="relative" onClick={(e) => e.stopPropagation()}>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search clients by name or email..."
                        value={clientSearchQuery}
                        onChange={(e) => {
                          setClientSearchQuery(e.target.value);
                          setShowClientDropdown(true);
                        }}
                        onFocus={() => setShowClientDropdown(true)}
                        className="w-full pl-10 pr-3 py-2 border-2 border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-900 transition-all duration-150"
                      />
                    </div>

                    {/* Searchable Dropdown */}
                    {showClientDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 shadow-lg max-h-60 overflow-y-auto">
                        {sortedAndFilteredClients.length > 0 ? (
                          sortedAndFilteredClients.map(client => (
                            <button
                              key={client.id}
                              onClick={() => {
                                setSelectedClient(client.email);
                                setClientSearchQuery(`${client.name} (${client.email})`);
                                setShowClientDropdown(false);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex flex-col">
                                <span className="font-medium text-gray-900">{client.name}</span>
                                <span className="text-sm text-gray-600">{client.email}</span>
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-4 text-center text-sm text-gray-500">
                            No clients found
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Display selected client */}
                  {selectedClient && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 text-sm">
                      <span className="font-medium text-blue-800">Selected: </span>
                      <span className="text-blue-700">{clientSearchQuery}</span>
                    </div>
                  )}

                  {clientsData.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">No clients available</p>
                  )}
                </div>

                {selectedClient && (
                  <div className="pt-4 border-t border-gray-200">
                    <SendEmailInbound
                      recipientEmail={selectedClient}
                      onClose={handleCloseEmailModal}
                      sendWithInboundInfo={false}
                    />
                  </div>
                )}
              </div>

              {!selectedClient && (
                <div className="flex justify-end space-x-2 mt-6 pt-4 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={handleCloseEmailModal}
                    className="px-4 py-2"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InboundSidebar;